import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { artworkService, studentService } from '../../services';
import type { ArtworkResponse } from '../../services/artworkService';
import { useArtworkObjectUrlState } from '../../hooks/useArtworkObjectUrl';

export interface ArtworkLightboxProps {
  artwork: ArtworkResponse | null;
  artworks?: ArtworkResponse[];
  currentIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  galleryMode?: boolean;
  /** The exact button/card that activated the viewer, when known. */
  activatingElement?: HTMLElement | null;
  /** A route that the caller has already authorized with the public endpoint. */
  portfolioHref?: string;
  /** Called before the verified portfolio route is followed. */
  onPortfolioNavigate?: (artwork: ArtworkResponse) => void;
}

type GestureMode = 'idle' | 'swipe' | 'pan' | 'pinch' | 'cancelled';

interface GestureState {
  mode: GestureMode;
  identifiers: number[];
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  startScale: number;
  startPanX: number;
  startPanY: number;
  startDistance: number;
  moved: boolean;
  startedAt: number;
}

const emptyGesture = (): GestureState => ({
  mode: 'idle',
  identifiers: [],
  startX: 0,
  startY: 0,
  lastX: 0,
  lastY: 0,
  startScale: 1,
  startPanX: 0,
  startPanY: 0,
  startDistance: 0,
  moved: false,
  startedAt: 0,
});

const MAX_PUBLIC_STUDENT_ID = 2_147_483_647;

function parsePublicStudentId(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > MAX_PUBLIC_STUDENT_ID) {
    return null;
  }
  return value;
}

function point(touch: React.Touch): { x: number; y: number } {
  return { x: touch.clientX, y: touch.clientY };
}

function distance(a: React.Touch, b: React.Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function touchByIdentifier(touches: React.TouchList, identifier: number): React.Touch | null {
  for (let index = 0; index < touches.length; index += 1) {
    const touch = typeof touches.item === 'function'
      ? touches.item(index)
      : (touches as unknown as ArrayLike<React.Touch>)[index] ?? null;
    if (touch?.identifier === identifier) return touch;
  }
  return null;
}

function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  );
}

function focusableOpener(element: HTMLElement | null): element is HTMLElement {
  if (!element || !element.isConnected || element.ownerDocument !== document) return false;
  let ancestor: HTMLElement | null = element;
  while (ancestor) {
    if (
      ancestor.getAttribute('aria-hidden') === 'true' ||
      ancestor.hasAttribute('hidden') ||
      ancestor.hasAttribute('inert') ||
      (ancestor as HTMLElement & { inert?: boolean }).inert
    ) return false;
    const styles = window.getComputedStyle(ancestor);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;
    ancestor = ancestor.parentElement;
  }
  if ('disabled' in element && (element as HTMLButtonElement).disabled) return false;
  const rect = element.getBoundingClientRect();
  const hasBox = rect.width > 0 || rect.height > 0 || element.getClientRects().length > 0;
  const documentRect = document.documentElement.getBoundingClientRect();
  const documentHasLayout = documentRect.width > 0 || documentRect.height > 0 || document.documentElement.getClientRects().length > 0;
  if (documentHasLayout && !hasBox) return false;
  return true;
}

function focusSafeFallback(): void {
  const body = document.body;
  const hadTabIndex = body.hasAttribute('tabindex');
  const previousTabIndex = body.getAttribute('tabindex');
  body.setAttribute('tabindex', '-1');
  body.focus({ preventScroll: true });
  if (hadTabIndex) body.setAttribute('tabindex', previousTabIndex ?? '');
  else body.removeAttribute('tabindex');
}

const ViewerDialog = styled.dialog`
  width: 100vw;
  height: 100dvh;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  background: transparent;
  overflow: hidden;

  &::backdrop {
    background: ${({ theme }) => theme.colors.mobile.overlay};
  }

  &[open] {
    display: grid;
    place-items: center;
  }
`;

const ViewerFrame = styled.div`
  display: flex;
  flex-direction: column;
  width: min(96vw, 1180px);
  height: min(92dvh, 860px);
  min-height: 0;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.default};
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.gallery.background};
  box-shadow: 0 24px 90px rgba(0, 0, 0, 0.48);

  @media (min-width: 800px) {
    flex-direction: row;
  }
`;

const ImageColumn = styled.section`
  position: relative;
  display: grid;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  place-items: center;
  background: ${({ theme }) => theme.colors.gallery.mat};

  @media (max-width: 799px) {
    display: flex;
    flex-direction: column;
    flex: 1 1 62%;
  }
`;

const ImageSurface = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  overflow: hidden;
  touch-action: none;
  user-select: none;

  @media (max-width: 799px) {
    position: relative;
    inset: auto;
    flex: 1 1 auto;
    width: 100%;
    min-height: 0;
  }
`;

const ImageFrame = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  padding: 56px 72px;

  @media (max-width: 799px) {
    padding: 72px 12px 16px;
  }
`;

const ArtworkImage = styled.img`
  display: block;
  flex: 0 1 auto;
  width: auto;
  height: auto;
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transform-origin: center center;
  will-change: transform;
  user-select: none;
  -webkit-user-drag: none;
`;

const CloseButton = styled.button`
  position: absolute;
  z-index: 2;
  top: max(12px, env(safe-area-inset-top));
  right: max(12px, env(safe-area-inset-right));
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.default};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  font-size: 1.45rem;
  cursor: pointer;
`;

const NavButton = styled.button<{ $side: 'previous' | 'next' }>`
  position: absolute;
  z-index: 2;
  top: 50%;
  ${({ $side }) => ($side === 'previous' ? 'left: 12px;' : 'right: 12px;')}
  transform: translateY(-50%);
  display: grid;
  width: 48px;
  height: 56px;
  place-items: center;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.default};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  font-size: 1.85rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.34;
    cursor: default;
  }

  @media (max-width: 799px) {
    position: static;
    transform: none;
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
  }
`;

const NavigationRail = styled.div`
  display: contents;

  @media (max-width: 799px) {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 8px 12px max(8px, env(safe-area-inset-bottom));
  }
`;

const Details = styled.aside`
  flex: 0 0 min(100%, 330px);
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 28px 24px max(24px, env(safe-area-inset-bottom));
  background: ${({ theme }) => theme.colors.gallery.surface };

  @media (max-width: 799px) {
    flex-basis: 38%;
    min-height: 0;
  }
`;

const Status = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  line-height: 1.5;
`;

const ZoomStatus = styled(Status)`
  margin-top: 8px;
`;

const DetailsHeading = styled.h2`
  margin: 0 0 10px;
  font-size: 1.2rem;
  line-height: 1.3;
`;

const Metadata = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 14px;
  margin: 18px 0;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: 0.92rem;

  dt { color: ${({ theme }) => theme.colors.gallery.text.tertiary}; }
  dd { margin: 0; }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
`;

const Tag = styled.span`
  padding: 4px 8px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.gallery.text.inverse};
  background: ${({ theme }) => theme.colors.gallery.accent.secondary};
  font-size: 0.8rem;
`;

const Action = styled.button`
  min-height: 44px;
  margin-top: 14px;
  padding: 8px 14px;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.strong};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  background: transparent;
  cursor: pointer;
`;

const PortfolioLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  margin-top: 14px;
  padding: 8px 14px;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.gallery.text.inverse};
  background: ${({ theme }) => theme.colors.gallery.accent.primary};
  text-decoration: none;
`;

export const ArtworkLightbox: React.FC<ArtworkLightboxProps> = ({
  artwork,
  artworks,
  currentIndex,
  isOpen,
  onClose,
  onNavigate,
  activatingElement,
  portfolioHref,
  onPortfolioNavigate,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const sessionActiveRef = useRef(false);
  const sessionIdRef = useRef(0);
  const closeReasonRef = useRef<'normal' | 'forced'>('normal');
  const previousOverflowRef = useRef<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageAttempt, setImageAttempt] = useState(0);
  const [zoomScale, setZoomScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [portfolioResult, setPortfolioResult] = useState<{
    href: string;
    sessionId: number;
    artworkId: number | null;
    studentId: number | null;
  } | null>(null);
  const [verifiedArtwork, setVerifiedArtwork] = useState<ArtworkResponse | null>(null);
  const [verificationError, setVerificationError] = useState(false);
  const [verificationAttempt, setVerificationAttempt] = useState(0);
  const mediaKeyRef = useRef('');
  const currentArtworkIdRef = useRef<number | null>(artwork?.artwork_id ?? null);
  const gestureRef = useRef<GestureState>(emptyGesture());
  const tapRef = useRef<{ time: number; x: number; y: number } | null>(null);
  const imageSurfaceRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const requestedArtworkId = artwork?.artwork_id ?? null;

  useEffect(() => {
    if (!isOpen || requestedArtworkId === null) {
      setVerifiedArtwork(null);
      setVerificationError(false);
      return;
    }

    let cancelled = false;
    setVerifiedArtwork(null);
    setVerificationError(false);
    artworkService.getArtwork(requestedArtworkId)
      .then((freshArtwork) => {
        if (!cancelled && freshArtwork.artwork_id === requestedArtworkId) setVerifiedArtwork(freshArtwork);
      })
      .catch(() => {
        if (!cancelled) setVerificationError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, requestedArtworkId, verificationAttempt]);

  // Keep one native dialog session while a navigated item is checked. The
  // response must match the current prop before any old metadata or image is
  // rendered for that item.
  const displayArtwork = verifiedArtwork?.artwork_id === artwork?.artwork_id ? verifiedArtwork : null;
  const active = isOpen && artwork !== null;
  const verificationPending = active && displayArtwork === null && !verificationError;
  const loadedArtworks = artworks ?? [];
  const itemCount = loadedArtworks.length;
  const boundedIndex = itemCount > 0 ? Math.max(0, Math.min(currentIndex ?? 0, itemCount - 1)) : 0;
  const isFirst = boundedIndex <= 0;
  const isLast = itemCount === 0 || boundedIndex >= itemCount - 1;

  const clearTapHistory = useCallback(() => {
    tapRef.current = null;
  }, []);

  const resetGesture = useCallback(() => {
    gestureRef.current = emptyGesture();
    setPan({ x: 0, y: 0 });
  }, []);

  const resetMedia = useCallback(() => {
    currentArtworkIdRef.current = artwork?.artwork_id ?? null;
    setImageLoaded(false);
    setImageError(false);
    setImageAttempt(0);
    setZoomScale(1);
    resetGesture();
    clearTapHistory();
  }, [artwork?.artwork_id, resetGesture, clearTapHistory]);

  const clampPan = useCallback((candidate: { x: number; y: number }, scale: number) => {
    if (scale <= 1) return { x: 0, y: 0 };
    const surface = imageSurfaceRef.current;
    const image = imageRef.current;
    if (!surface || !image) return candidate;
    const surfaceBox = surface.getBoundingClientRect();
    const imageBox = image.getBoundingClientRect();
    const baseWidth = imageBox.width / Math.max(1, zoomScale);
    const baseHeight = imageBox.height / Math.max(1, zoomScale);
    const maxX = Math.max(0, (baseWidth * scale - surfaceBox.width) / 2);
    const maxY = Math.max(0, (baseHeight * scale - surfaceBox.height) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, candidate.x)),
      y: Math.max(-maxY, Math.min(maxY, candidate.y)),
    };
  }, [zoomScale]);

  useEffect(() => {
    resetMedia();
  }, [artwork?.artwork_id, artwork?.updated_at, resetMedia]);

  const restoreOpenerFocus = useCallback(() => {
    if (closeReasonRef.current === 'normal' && focusableOpener(openerRef.current)) {
      openerRef.current?.focus({ preventScroll: true });
    }
    openerRef.current = null;
  }, []);

  const closeNativeDialog = useCallback((reason: 'normal' | 'forced') => {
    const dialog = dialogRef.current;
    closeReasonRef.current = reason;
    const canRestoreOpener = reason === 'normal' && focusableOpener(openerRef.current);
    if (dialog?.open) {
      dialog.close();
      if (reason === 'forced' || !canRestoreOpener) focusSafeFallback();
    }
    if (sessionActiveRef.current) {
      sessionActiveRef.current = false;
      if (previousOverflowRef.current !== null) {
        document.body.style.overflow = previousOverflowRef.current;
        previousOverflowRef.current = null;
      }
      restoreOpenerFocus();
    }
  }, [restoreOpenerFocus]);

  useLayoutEffect(() => {
    if (!active) {
      if (sessionActiveRef.current) closeNativeDialog('forced');
      return;
    }
    if (sessionActiveRef.current) return;

    const dialog = dialogRef.current;
    if (!dialog) return;
    openerRef.current = activatingElement && focusableOpener(activatingElement)
      ? activatingElement
      : focusableOpener(document.activeElement as HTMLElement | null)
        ? document.activeElement as HTMLElement
        : null;
    previousOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    sessionIdRef.current += 1;
    sessionActiveRef.current = true;
    closeReasonRef.current = 'normal';
    if (!dialog.open) {
      dialog.showModal();
    }
    closeButtonRef.current?.focus({ preventScroll: true });

  }, [active, activatingElement, closeNativeDialog]);

  useEffect(() => () => {
    if (sessionActiveRef.current) closeNativeDialog('forced');
  }, [closeNativeDialog]);

  useEffect(() => {
    const currentSession = sessionIdRef.current;
    const controller = new AbortController();
    let cancelled = false;
    setPortfolioResult(null);
    if (!active || !displayArtwork) return () => controller.abort();
    const requestedArtworkId = displayArtwork.artwork_id;
    if (portfolioHref) {
      setPortfolioResult({
        href: portfolioHref,
        sessionId: currentSession,
        artworkId: requestedArtworkId,
        studentId: parsePublicStudentId(displayArtwork.student_id),
      });
      return () => controller.abort();
    }
    const studentId = parsePublicStudentId(displayArtwork.student_id);
    if (studentId === null) return () => controller.abort();
    studentService.getPublicStudent(studentId, controller.signal)
      .then((student) => {
        if (
          cancelled ||
          controller.signal.aborted ||
          sessionIdRef.current !== currentSession ||
          !active ||
          displayArtwork.artwork_id !== requestedArtworkId
        ) return;
        if (student.student_id === studentId && student.is_visible === true && displayArtwork.student_id === studentId) {
          setPortfolioResult({
            href: `/artists/${studentId}`,
            sessionId: currentSession,
            artworkId: requestedArtworkId,
            studentId,
          });
        }
      })
      .catch(() => {
        if (
          !cancelled &&
          sessionIdRef.current === currentSession &&
          displayArtwork.artwork_id === requestedArtworkId
        ) setPortfolioResult(null);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [active, displayArtwork, portfolioHref]);

  const move = useCallback((direction: -1 | 1) => {
    const nextIndex = boundedIndex + direction;
    if (nextIndex < 0 || nextIndex >= itemCount) return;
    onNavigate?.(nextIndex > boundedIndex ? 'next' : 'prev');
  }, [boundedIndex, itemCount, onNavigate]);

  const requestClose = useCallback(() => {
    closeNativeDialog('normal');
    onClose();
  }, [closeNativeDialog, onClose]);

  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) return;
      if (event.key === 'ArrowLeft' && !isFirst) {
        event.preventDefault();
        move(-1);
      } else if (event.key === 'ArrowRight' && !isLast) {
        event.preventDefault();
        move(1);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        requestClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [active, isFirst, isLast, move, requestClose]);

  const isCurrentMediaEvent = useCallback((mediaKey: string, artworkId: number) => (
    mediaKeyRef.current === mediaKey && currentArtworkIdRef.current === artworkId
  ), []);

  const beginPinch = useCallback((touches: React.TouchList) => {
    const first = typeof touches.item === 'function'
      ? touches.item(0)
      : (touches as unknown as ArrayLike<React.Touch>)[0] ?? null;
    const second = typeof touches.item === 'function'
      ? touches.item(1)
      : (touches as unknown as ArrayLike<React.Touch>)[1] ?? null;
    if (!first || !second) return;
    const firstPoint = point(first);
    gestureRef.current = {
      ...emptyGesture(),
      mode: 'pinch',
      identifiers: [first.identifier, second.identifier],
      startX: firstPoint.x,
      startY: firstPoint.y,
      lastX: firstPoint.x,
      lastY: firstPoint.y,
      startScale: zoomScale,
      startPanX: pan.x,
      startPanY: pan.y,
      startDistance: Math.max(1, distance(first, second)),
      startedAt: performance.now(),
    };
    clearTapHistory();
  }, [clearTapHistory, pan.x, pan.y, zoomScale]);

  const onTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof Node) || !event.currentTarget.contains(target)) return;
    if (target instanceof Element && target.closest('button,a')) return;
    event.preventDefault();
    if (event.touches.length >= 2) {
      beginPinch(event.touches);
      return;
    }
    if (event.touches.length !== 1) return;
    const touch = typeof event.touches.item === 'function'
      ? event.touches.item(0)
      : (event.touches as unknown as ArrayLike<React.Touch>)[0] ?? null;
    if (!touch) return;
    const position = point(touch);
    gestureRef.current = {
      ...emptyGesture(),
      mode: 'swipe',
      identifiers: [touch.identifier],
      startX: position.x,
      startY: position.y,
      lastX: position.x,
      lastY: position.y,
      startScale: zoomScale,
      startPanX: pan.x,
      startPanY: pan.y,
      startedAt: performance.now(),
    };
  }, [beginPinch, pan.x, pan.y, zoomScale]);

  const onTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const state = gestureRef.current;
    if (state.mode === 'idle' || state.mode === 'cancelled') return;
    event.preventDefault();
    if (state.mode === 'pinch') {
      const first = touchByIdentifier(event.touches, state.identifiers[0]);
      const second = touchByIdentifier(event.touches, state.identifiers[1]);
      if (!first || !second) {
        state.mode = 'cancelled';
        clearTapHistory();
        return;
      }
      const nextScale = Math.max(1, Math.min(4, state.startScale * distance(first, second) / state.startDistance));
      setZoomScale(nextScale);
      setPan(nextScale === 1 ? { x: 0, y: 0 } : clampPan({ x: state.startPanX, y: state.startPanY }, nextScale));
      state.moved = true;
      return;
    }
    const touch = touchByIdentifier(event.touches, state.identifiers[0]);
    if (!touch) {
      state.mode = 'cancelled';
      clearTapHistory();
      return;
    }
    const nextPoint = point(touch);
    const deltaX = nextPoint.x - state.startX;
    const deltaY = nextPoint.y - state.startY;
    if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) state.moved = true;
    state.lastX = nextPoint.x;
    state.lastY = nextPoint.y;
    if (state.mode === 'pan' || (zoomScale > 1 && state.moved)) {
      state.mode = 'pan';
      setPan(clampPan({ x: state.startPanX + deltaX, y: state.startPanY + deltaY }, zoomScale));
    }
  }, [clearTapHistory, clampPan, zoomScale]);

  const onTouchEnd = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const state = gestureRef.current;
    if (state.mode === 'idle') return;
    const activeIds = state.identifiers.filter(id => touchByIdentifier(event.touches, id));
    if (state.mode === 'pinch') {
      clearTapHistory();
      state.mode = event.touches.length === 0 ? 'idle' : 'cancelled';
      if (event.touches.length === 1) state.identifiers = [];
      return;
    }
    if (event.touches.length > 0 || activeIds.length > 0) return;
    const dx = state.lastX - state.startX;
    const dy = state.lastY - state.startY;
    const elapsed = performance.now() - state.startedAt;
    const isSwipe = state.mode === 'swipe' && zoomScale === 1 && Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.2;
    if (isSwipe) {
      clearTapHistory();
      if (dx < 0) move(1);
      else move(-1);
    } else if (!state.moved && state.mode === 'swipe' && elapsed < 320) {
      const now = performance.now();
      const previous = tapRef.current;
      if (previous && now - previous.time < 300 && Math.hypot(previous.x - state.lastX, previous.y - state.lastY) < 32) {
        setZoomScale(current => current > 1 ? 1 : 2);
        setPan({ x: 0, y: 0 });
        clearTapHistory();
      } else {
        tapRef.current = { time: now, x: state.lastX, y: state.lastY };
      }
    } else {
      clearTapHistory();
    }
    gestureRef.current = emptyGesture();
  }, [clearTapHistory, move, zoomScale]);

  const onTouchCancel = useCallback(() => {
    gestureRef.current.mode = 'cancelled';
    gestureRef.current.identifiers = [];
    clearTapHistory();
    resetGesture();
  }, [clearTapHistory, resetGesture]);

  const retryImage = useCallback(() => {
    setImageError(false);
    setImageLoaded(false);
    setImageAttempt(attempt => attempt + 1);
  }, []);

  const retryVerification = useCallback(() => {
    setVerifiedArtwork(null);
    resetMedia();
    setVerificationError(false);
    setVerificationAttempt((attempt) => attempt + 1);
  }, [resetMedia]);

  const imageMediaKey = `${displayArtwork?.artwork_id ?? 'none'}:${displayArtwork?.updated_at ?? 'none'}:${imageAttempt}`;
  useLayoutEffect(() => {
    mediaKeyRef.current = imageMediaKey;
  }, [imageMediaKey]);
  const imageArtworkId = displayArtwork?.artwork_id;
  const imageState = useArtworkObjectUrlState(
    displayArtwork?.artwork_id,
    'image',
    displayArtwork?.updated_at,
    'public',
    active && displayArtwork !== null,
    imageAttempt,
  );
  const imageSrc = imageState.url;

  useEffect(() => {
    if (imageState.error && imageArtworkId !== undefined && isCurrentMediaEvent(imageMediaKey, imageArtworkId)) {
      setImageLoaded(false);
      setImageError(true);
    }
  }, [imageArtworkId, imageMediaKey, imageState.error, isCurrentMediaEvent]);

  return (
    <ViewerDialog
      ref={dialogRef}
      aria-labelledby="artwork-lightbox-title"
      onCancel={(event) => {
        event.preventDefault();
        requestClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
      data-testid="artwork-lightbox"
    >
      {active && (
        <ViewerFrame>
          <ImageColumn>
            <CloseButton ref={closeButtonRef} type="button" aria-label="Close lightbox" onClick={requestClose}>×</CloseButton>
            <ImageSurface
              ref={imageSurfaceRef}
              aria-label="Artwork image"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onTouchCancel={onTouchCancel}
            >
              <ImageFrame>
                {displayArtwork && !imageError && (
                  <ArtworkImage
                    ref={imageRef}
                    key={`${displayArtwork.artwork_id}-${displayArtwork.updated_at}-${imageAttempt}`}
                    src={imageSrc || undefined}
                    alt={displayArtwork.student_name ? `Artwork by ${displayArtwork.student_name}` : 'Student artwork'}
                    draggable={false}
                    onLoad={() => {
                      if (imageArtworkId !== undefined && isCurrentMediaEvent(imageMediaKey, imageArtworkId)) setImageLoaded(true);
                    }}
                    onError={() => {
                      if (imageArtworkId !== undefined && isCurrentMediaEvent(imageMediaKey, imageArtworkId)) setImageError(true);
                    }}
                    style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomScale})` }}
                  />
                )}
                {displayArtwork && imageError && (
                  <Status role="alert">
                    This artwork image could not be loaded.
                    <br />
                    <Action type="button" onClick={retryImage}>Retry image</Action>
                  </Status>
                )}
                {!displayArtwork && verificationError && (
                  <Status role="alert">
                    This artwork is no longer available.
                    <br />
                    <Action type="button" onClick={retryVerification}>Retry artwork</Action>
                  </Status>
                )}
                {!displayArtwork && verificationPending && <Status role="status">Checking artwork…</Status>}
                {displayArtwork && !imageError && !imageLoaded && <Status role="status">Loading artwork…</Status>}
              </ImageFrame>
            </ImageSurface>
            <NavigationRail>
              <NavButton type="button" $side="previous" aria-label="Previous artwork" onClick={() => move(-1)} disabled={!displayArtwork || isFirst}>‹</NavButton>
              <NavButton type="button" $side="next" aria-label="Next artwork" onClick={() => move(1)} disabled={!displayArtwork || isLast}>›</NavButton>
            </NavigationRail>
          </ImageColumn>
          <Details>
            <DetailsHeading id="artwork-lightbox-title">Artwork</DetailsHeading>
            <Status>{itemCount > 0 ? `${boundedIndex + 1} of ${itemCount} loaded works` : 'No loaded works'}</Status>
            <ZoomStatus>Zoom {Math.round(zoomScale * 100)}%</ZoomStatus>
            {displayArtwork?.student_name && (
              <Metadata>
                <dt>Artist</dt><dd>{displayArtwork.student_name}</dd>
                {displayArtwork.uploaded_at && <><dt>Added</dt><dd>{new Date(displayArtwork.uploaded_at).toLocaleDateString()}</dd></>}
              </Metadata>
            )}
            {displayArtwork && displayArtwork.tags.length > 0 && (
              <Tags aria-label="Artwork tags">{displayArtwork.tags.map(tag => <Tag key={tag}>{tag}</Tag>)}</Tags>
            )}
            {portfolioResult &&
              displayArtwork &&
              active &&
              portfolioResult.sessionId === sessionIdRef.current &&
              portfolioResult.artworkId === displayArtwork.artwork_id &&
              (portfolioResult.studentId === null || portfolioResult.studentId === displayArtwork.student_id) &&
              <PortfolioLink
                to={portfolioResult.href}
                onClick={() => onPortfolioNavigate?.(displayArtwork)}
              >
                View portfolio
              </PortfolioLink>}
          </Details>
        </ViewerFrame>
      )}
    </ViewerDialog>
  );
};

export default ArtworkLightbox;
